import type { Guide } from "./types.js";
import { Lead, H2, P, Ul, Li, AppLink, C, Callout } from "./primitives.js";

export const meta = {
  id: "task-management",
  title: "Task management + bulk cleanup",
  category: "Workflows",
  lastUpdated: "2026-05-23",
  description: "Tasks page is auto-derived from keyword state. Edit/archive/reject inline or in bulk.",
  relatedGuides: ["produce-article"],
  readTimeMin: 4,
} as const;

export function Body() {
  return (
    <>
      <Lead>
        The <AppLink to="tasks">Tasks</AppLink> page is an <strong>auto-derived inbox</strong> of pending pipeline actions. Every row corresponds to a real keyword that hasn't progressed past a certain stage. There's no "task table" to mutate — to remove a task, you change the underlying keyword's state.
      </Lead>

      <H2>Task categories</H2>
      <Ul>
        <Li><strong>Ready to distribute</strong> — article produced but not yet sent to any channel.</Li>
        <Li><strong>Ready to produce</strong> — brief generated, awaiting article production.</Li>
        <Li><strong>Needs brief</strong> — competitor extracts present, brief not yet generated.</Li>
        <Li><strong>Needs competitor scrape</strong> — SerpAPI snapshot present but no usable extracts. If a scrape already ran and every fetch failed, the row says <strong>Retry competitor scrape</strong> and names the failure count.</Li>
        <Li><strong>Needs enrichment</strong> — bare keyword, never enriched.</Li>
        <Li><strong>Refresh stale articles</strong> — articles whose <C>lastReviewedAt</C> is &gt;30 days old.</Li>
      </Ul>
      <P>
        The four research stages only appear on keywords that have no article yet. Once a
        piece is written those steps are history, not outstanding work — and guest posts,
        which never pass through SERP research at all, are exempt from them entirely.
      </P>

      <H2>Row-level editing</H2>
      <P>Every row has inline editors:</P>
      <Ul>
        <Li><strong>Priority pill</strong> (urgent / high / medium / low) — click to change. Saves immediately.</Li>
        <Li><strong>Cluster dropdown</strong> — re-assign to a different cluster from the row.</Li>
        <Li><strong>Open</strong> button — jumps to Pipeline detail panel.</Li>
        <Li><strong>Archive icon</strong> (right side) — soft-deletes the keyword. Row disappears from Tasks + Pipeline by default but survives in the DB.</Li>
      </Ul>

      <H2>Bulk operations</H2>
      <P>Each row has a checkbox. Select 1+ and a sticky action bar appears at the top:</P>
      <Ul>
        <Li><strong>Set priority</strong> — applies to all selected.</Li>
        <Li><strong>Move to cluster</strong> — re-cluster all selected (or "clear cluster" to set null).</Li>
        <Li><strong>Archive</strong> — soft-delete all selected. Confirms with a count.</Li>
        <Li><strong>Reject</strong> — prompts for an optional reason, sticks across re-runs.</Li>
        <Li><strong>Hard delete</strong> — red, two-step confirm. Cascades to extracts/distributions. <strong>NOT reversible.</strong></Li>
      </Ul>
      <P>The "Select all visible" link selects every row in the current filter (across all category groups).</P>

      <H2>Archive vs Reject vs Delete</H2>
      <Ul>
        <Li><strong>Archive</strong> — sets <C>status="archived"</C>. Soft delete. Reversible by flipping status back via the Pipeline detail panel. Use for routine cleanup of test data or rows you might want again.</Li>
        <Li><strong>Reject</strong> — sets <C>status="rejected"</C> + captures a <C>rejectionReason</C>. Semantically "we deliberately decided not to write this". Reversible but more deliberate.</Li>
        <Li><strong>Hard delete</strong> — <C>DELETE FROM keywords</C>. Cascades to <C>competitor_extracts</C>, <C>distributions</C>. Use for genuinely-mistaken rows (test data you don't want lingering in the archive).</Li>
      </Ul>

      <H2>Full editing in the Pipeline detail panel</H2>
      <P>
        For richer edits, click <strong>Open</strong> to land in the keyword's detail panel. The Overview tab has inline editors for priority, status, cluster, notes, and (when status=rejected) rejection reason. A <strong>Danger zone</strong> at the bottom has Archive + Reject + Hard-delete buttons with state-aware labels (e.g. "Already archived" when applicable).
      </P>

      <Callout kind="tip">
        For first-time cleanup: filter Tasks to "Needs enrichment" → click "Select all visible" → Archive in the bulk bar. Clears most leftover seed/test data in one shot.
      </Callout>
    </>
  );
}

export const guide: Guide = { meta, Body };
